/**
 * Pure display helpers for the `/profile` stats.
 */

import { tForLocale, type Locale } from '@/i18n/locale';

type DurationUnit = 'year' | 'month' | 'day' | 'hour' | 'minute';
type DurationParts = Partial<Record<`${DurationUnit}s`, number>>;

const DAY = 24 * 60;
/** A calendar-average month, so 12 of them are a 365-day year. */
const MONTH_DAYS = 365 / 12;

/** `Intl.DurationFormat` where the browser has it (not yet in TS's lib). */
type DurationFormatCtor = new (
  locale: string,
  options: { style: 'long' | 'narrow' },
) => { format: (parts: DurationParts) => string };

function intlLocale(locale: Locale): string {
  return locale === 'pt-BR' ? 'pt-BR' : 'en-US';
}

/**
 * The same output as `Intl.DurationFormat` for the units we use: each unit
 * via `NumberFormat` `unit` style, joined with a `unit` `ListFormat`.
 */
function formatParts(parts: DurationParts, style: 'long' | 'narrow', locale: Locale): string {
  const tag = intlLocale(locale);
  const DurationFormat = (Intl as unknown as { DurationFormat?: DurationFormatCtor })
    .DurationFormat;
  // DurationFormat drops zero fields, so "0m" would come back empty.
  if (DurationFormat && Object.values(parts).some((n) => n > 0)) {
    return new DurationFormat(tag, { style }).format(parts);
  }
  const pieces = (Object.entries(parts) as [`${DurationUnit}s`, number][]).map(([key, n]) =>
    new Intl.NumberFormat(tag, {
      style: 'unit',
      unit: key.slice(0, -1),
      unitDisplay: style,
    }).format(n),
  );
  return new Intl.ListFormat(tag, { type: 'unit', style }).format(pieces);
}

/**
 * Split minutes into at most two ADJACENT units, led by the largest one that
 * is at least 1 (ui-ux.md §2 "Numbers, dates and durations"). Under a day the
 * pair is hours + minutes; from a day up it is years/months/days/hours, a
 * month being 365/12 days. A zero second unit is dropped ("1 year", not
 * "1 year, 0 months").
 */
export function durationParts(minutes: number): DurationParts {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return { minutes: m };
  if (m < DAY) return pair('hours', Math.floor(m / 60), 'minutes', m % 60);
  const days = Math.floor(m / DAY);
  if (days < 31) return pair('days', days, 'hours', Math.floor((m % DAY) / 60));
  if (days < 365) {
    const months = Math.floor(days / MONTH_DAYS);
    return pair('months', months, 'days', Math.floor(days - months * MONTH_DAYS));
  }
  const years = Math.floor(days / 365);
  return pair('years', years, 'months', Math.floor((days % 365) / MONTH_DAYS));
}

function pair(
  a: `${DurationUnit}s`,
  av: number,
  b: `${DurationUnit}s`,
  bv: number,
): DurationParts {
  return bv > 0 ? { [a]: av, [b]: bv } : { [a]: av };
}

/**
 * Every duration on the stats surface — runtime, a day/week in the heatmap
 * and trend, a genre's total, lifetime watch time. Under a day it reads like
 * a timecode in the narrow style ("2h 15m" / "2 h 15 min"); from a day up it
 * spells the units out ("5 days, 4 hours", "18 years, 6 months" / "18 anos
 * e 6 meses").
 */
export function fmtDuration(minutes: number, locale: Locale = 'en'): string {
  const style = Math.round(minutes) < DAY ? 'narrow' : 'long';
  return formatParts(durationParts(minutes), style, locale);
}

/**
 * A count for a dense spot (a stat tile): compact above 10 000 ("244K" /
 * "244 mil"), exact below. Pair it with `fmtCount` in the accessible name.
 */
export function fmtCompactCount(n: number, locale: Locale = 'en'): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    notation: n >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(n);
}

/** The exact, grouped count ("244,289" / "244.289"). */
export function fmtCount(n: number, locale: Locale = 'en'): string {
  return new Intl.NumberFormat(intlLocale(locale)).format(n);
}

/** One or two initials for the avatar fallback ("Leo Viveiros" → "LV"). */
export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const letters = parts.slice(0, 2).map((part) => [...part][0] ?? '');
  return letters.join('').toUpperCase();
}

/**
 * Rename-cooldown end instant — "14:32" when today, "Aug 17, 14:32" later.
 * Port of the app's `fmtUntilTime` (`app/src/utils/format.ts`) so the edit
 * screen speaks the same cooldown language as the app.
 */
export function fmtUntilTime(iso: string | null, dateLocale: string = 'en-US'): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' });
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return time;
  const dateOpts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== now.getFullYear()) dateOpts.year = 'numeric';
  return `${d.toLocaleDateString(dateLocale, dateOpts)}, ${time}`;
}

/** ISO-8601 → epoch millis; `NaN` for anything unparseable. */
function timestamp(iso: string): number {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? NaN : t;
}

/** "just now", "12m ago", "3h ago", "2d ago", then a short calendar date. */
export function relativeTime(
  iso: string,
  now: number = Date.now(),
  locale: Locale = 'en',
): string {
  const then = timestamp(iso);
  if (Number.isNaN(then)) return '';
  const diffMs = Math.max(0, now - then);
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return tForLocale(locale, 'time.justNow');
  if (minutes < 60) return tForLocale(locale, 'time.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return tForLocale(locale, 'time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return tForLocale(locale, 'time.daysAgo', { count: days });
  const date = new Date(then);
  const sameYear = date.getUTCFullYear() === new Date(now).getUTCFullYear();
  return date.toLocaleDateString(locale === 'pt-BR' ? 'pt-BR' : 'en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** Collapse a comment body to one line for a list snippet (CSS truncates). */
export function oneLineSnippet(body: string): string {
  return body.replace(/\s+/g, ' ').trim();
}

/**
 * Deep-link identity from a comment's normalized target string
 * (`MOVIE#969681` / `SHOW#1396#SEASON#1#EP#1` → the containing title), used
 * when the history route's lean title card is missing.
 */
export function targetFromString(
  target: string,
): { tmdbId: string; titleType: 'show' | 'movie' } | null {
  const [kind, id] = target.split('#');
  if ((kind !== 'SHOW' && kind !== 'MOVIE') || !id || !/^\d+$/.test(id)) return null;
  return { tmdbId: id, titleType: kind === 'MOVIE' ? 'movie' : 'show' };
}
